import type { Account, Owner, OwnerDetails } from "~/v2/api/rest/account";

// Matches gobroker's isAllowedToCreateSecondAccount - https://github.com/alpacahq/gobroker/blob/develop/service/account/account.go#L728
const ALLOWED_TRADE_ACCOUNT_STATUSES: string[] = [
  "ACTIVE",
  "ACCOUNT_APPROVED",
  "ACCOUNT_UPDATED",
];

export interface IRAEligibilityResult {
  isEligible: boolean;
  isLoading: boolean;
}

const ELIGIBLE: IRAEligibilityResult = { isEligible: true, isLoading: false };
const INELIGIBLE: IRAEligibilityResult = {
  isEligible: false,
  isLoading: false,
};
const LOADING: IRAEligibilityResult = { isEligible: false, isLoading: true };

export interface IsEligibleForIRAOnboardingParams {
  accounts: Account[] | undefined;
  tradeAccount: Account | undefined;
  tradeAccountDetails: Owner | OwnerDetails | undefined;
  isLegalEntity: boolean | undefined;
  isLoading: boolean;
}

/**
 * Determine if a user is eligible for IRA onboarding.
 */
export const isEligibleForIRAOnboarding = ({
  accounts,
  tradeAccount,
  tradeAccountDetails,
  isLegalEntity,
  isLoading,
}: IsEligibleForIRAOnboardingParams): IRAEligibilityResult => {
  if (isLoading || !accounts || !tradeAccountDetails) {
    return LOADING;
  }

  // Cannot be a legal entity - https://github.com/alpacahq/gobroker/blob/develop/service/account/account.go#L557
  if (isLegalEntity) {
    return INELIGIBLE;
  }

  // Must have a trading account - https://github.com/alpacahq/gobroker/blob/develop/service/account/account.go#L541
  if (!tradeAccount) {
    return INELIGIBLE;
  }

  if (!ALLOWED_TRADE_ACCOUNT_STATUSES.includes(tradeAccount.status)) {
    return INELIGIBLE;
  }

  // Must have USA tax residence
  if (tradeAccountDetails.country_of_tax_residence !== "USA") {
    return INELIGIBLE;
  }

  // Must have a USA tax ID type (SSN or ITIN)
  if (
    tradeAccountDetails.tax_id_type !== "USA_SSN" &&
    tradeAccountDetails.tax_id_type !== "USA_ITIN"
  ) {
    return INELIGIBLE;
  }

  const rothIraAccount = accounts.find(
    (account) =>
      account.account_type === "ira" && account.account_sub_type === "roth",
  );

  const traditionalIraAccount = accounts.find(
    (account) =>
      account.account_type === "ira" &&
      account.account_sub_type === "traditional",
  );

  // Cannot already have both types of IRA accounts - https://github.com/alpacahq/gobroker/blob/develop/service/account/account.go#L553
  if (rothIraAccount && traditionalIraAccount) {
    return INELIGIBLE;
  }

  return ELIGIBLE;
};
